import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDevice, useSensorData, useMTCommand } from '@/hooks';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { findPendingMT } from '@/lib/sensor-commands';
import { formatDateTime } from '@/lib/formatters';
import { resolveOnlineState, onlineStateVariant } from '@/lib/enum-resolvers';
import { isIntegratedSensor } from '@/lib/sensor-map';

export function ACShorePanel() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const { sensorData } = useSensorData();
  const [refreshing, setRefreshing] = useState(false);
  const { sendCommand, mtObjList } = useMTCommand(deviceUUID ?? '');
  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.GET_SENSOR_DATA_REQUEST);

  const acSensors = sensorData?.acSensorList ?? [];

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.GET_SENSOR_DATA_REQUEST);
    } finally {
      setRefreshing(false);
    }
  };

  if (deviceInfoLoading && !selectedDeviceInfo) {
    return <PageSpinner />;
  }

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description="Select a device to view AC shore power status."
      />
    );
  }

  if (acSensors.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">AC Shore Power</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Monitor shore power connection status
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            Refresh
          </Button>
        </div>
        <EmptyState
          title="No AC Sensors"
          description="No AC shore power sensors are configured on this device."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">AC Shore Power</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Monitor shore power connection status
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {acSensors.map((sensor, idx) => {
          const isConnected = sensor.acEnabled;
          // Same alarm-driven card tint as Intrusion/Bilge-Float/DC Banks —
          // AC only has one alert tier (no critical distinction), matching
          // its existing single "Alert" badge.
          const cardTint = sensor.alertPresent
            ? 'border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-900/10'
            : 'border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900';

          return (
            <div
              key={`${sensor.acNum}-${idx}`}
              className={`rounded-xl border p-6 ${cardTint}`}
            >
              {/* Header */}
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {sensor.descriptor || `AC ${sensor.acNum}`}
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {/* Mobile's "{sensorName}, Bank {N}" (updateACCategory,
                        DeviceCategoryMetrics.cs) — acNum is used raw, with NO +1
                        offset (unlike DC's bankInstance/security's zoneInstance). */}
                    {sensor.sensorName}, Bank {sensor.acNum}
                  </p>
                </div>
                {isIntegratedSensor(sensor) ? (
                  <StatusBadge label="Built-in" variant="info" dot />
                ) : (
                  <StatusBadge
                    label={resolveOnlineState(sensor.onlineState)}
                    variant={onlineStateVariant(sensor.onlineState)}
                    dot
                  />
                )}
              </div>

              {/* Connection status icon */}
              <div className="mb-5 flex flex-col items-center gap-3">
                <div
                  className={`flex h-20 w-20 items-center justify-center rounded-full ${
                    isConnected
                      ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  {/* Plug icon */}
                  <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Shore Power</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 border-t border-slate-100 pt-3 text-sm dark:border-navy-700">
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Sensor Present</span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {sensor.sensorPresent ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Alert</span>
                  {sensor.alertPresent ? (
                    <StatusBadge label="Alert" variant="danger" dot />
                  ) : (
                    <span className="font-medium text-slate-700 dark:text-slate-300">None</span>
                  )}
                </div>
              </div>

              {/* Updated time */}
              {sensor.updatedDT && (
                <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                  Updated: {formatDateTime(sensor.updatedDT)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
